import React, { useContext } from 'react';
import { Button, Card, Image } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faLock, faMobileAlt, faSignOutAlt, faUserTie } from '@fortawesome/free-solid-svg-icons';
import Logo from '../assets/Logo.png';
import { AuthContext } from '../context/AuthContext';
import { ChangeLanguageComponent } from '../components/ChangeLanguage';
import { Role, WEB_ALLOWED_ROLES } from '../utils/Enums/Roles';
import { ImpersonationBanner } from '../components/ImpersonationBanner';

export const NoAccessPage: React.FC = () => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { logOut, loggedUser, permissions, mainPage } = useContext(AuthContext);
    const navigate = useNavigate();
    const languages = ['en', 'bg'];

    const handleLogout = () => {
        logOut();
        navigate('/login', { replace: true });
    };

    const hasWebAccess = permissions && permissions.some((r) => WEB_ALLOWED_ROLES.includes(r as any));
    const userName = loggedUser?.name || loggedUser?.username || localStorage.getItem('username') || '';
    const userRoles = permissions?.filter(Boolean).join(', ') || Role.MANAGER;

    const fallbackHome = mainPage && mainPage !== '/no-access' ? mainPage : '/dashboard';

    return (
        <div className='d-flex flex-column justify-content-center align-items-center vh-100 w-100 bg-light position-relative p-3'>
            <div className='position-absolute top-0 start-0 w-100'>
                <ImpersonationBanner />
            </div>
            <div className='position-absolute top-0 end-0 p-4'>
                <ChangeLanguageComponent languages={languages} />
            </div>

            <Card className='shadow-lg border-0 rounded-4 p-4 text-center' style={{ maxWidth: '540px', width: '100%' }}>
                <Card.Body className='d-flex flex-column align-items-center'>
                    <Image src={Logo} alt='Logo' className='mb-4' style={{ maxHeight: '60px', objectFit: 'contain' }} />

                    <div
                        className={`d-flex align-items-center justify-content-center rounded-circle mb-3 ${
                            hasWebAccess ? 'bg-warning bg-opacity-10' : 'bg-primary bg-opacity-10'
                        }`}
                        style={{ width: '72px', height: '72px' }}
                    >
                        <FontAwesomeIcon
                            icon={hasWebAccess ? faLock : faMobileAlt}
                            size='2x'
                            className={hasWebAccess ? 'text-warning' : 'text-primary'}
                        />
                    </div>

                    <h4 className='fw-bold mb-2'>
                        {t(hasWebAccess ? 'accessDeniedTitle' : 'noWebAccessTitle')}
                    </h4>

                    <p className='text-muted mb-4 px-2' style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                        {t(hasWebAccess ? 'accessDeniedMessage' : 'noWebAccessMessage')}
                    </p>

                    <div className='w-100 p-3 mb-4 rounded-3 bg-light border text-start'>
                        <div className='d-flex align-items-center mb-1'>
                            <FontAwesomeIcon icon={faUserTie} className='text-secondary me-2' />
                            <span className='text-muted me-2'>{t('loggedInAs')}:</span>
                            <span className='fw-semibold text-dark'>{userName}</span>
                        </div>
                        <div className='d-flex align-items-center'>
                            <span className='badge bg-secondary text-white' style={{ fontSize: '0.8rem' }}>
                                {userRoles}
                            </span>
                        </div>
                    </div>

                    <div className='w-100 d-flex flex-column gap-2'>
                        {hasWebAccess && (
                            <Button
                                variant='primary'
                                className='w-100 py-2 d-flex align-items-center justify-content-center gap-2 color-main'
                                onClick={() => navigate(fallbackHome)}
                            >
                                <FontAwesomeIcon icon={faHome} />
                                <span>{t('backToHome')}</span>
                            </Button>
                        )}

                        <Button
                            variant={hasWebAccess ? 'outline-secondary' : 'outline-danger'}
                            className='w-100 py-2 d-flex align-items-center justify-content-center gap-2'
                            onClick={handleLogout}
                        >
                            <FontAwesomeIcon icon={faSignOutAlt} rotation={180} />
                            <span>{t('logoutAndSwitch')}</span>
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};
